import { Box } from "@mui/material";
import { useToast, NavbarFilters, type FilterMenuItem } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";
import { countByFilter } from "~/lib/filterCounts";
import {
  formatInventoryNavbarFilterSelectionMixpanelPayload,
  mixpanelTrack,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  parseProductTempZone,
  tempZoneToTemperatureZone
} from "~/lib/tempZone";
import { nonNullable } from "~/lib/typeHelpers";
import {
  selectIsAdjustingBins,
  selectHoldTypeFilters,
  selectSelectedInventoryRows,
  selectTempZoneFilters
} from "~/redux/selectors/inventorySelectors";

import {
  useGetAvailableHoldReasonCodesQuery,
  useGetInventoryFilterCountsQuery
} from "~/redux/warehouse/inventory.hooks";

import { ProductBinSearchInput } from "./ProductBinSearchInput";
import { setHoldTypeFilters, setTempZoneFilters } from "./inventory.slice";
import { useGetInventoryParams } from "./useGetInventoryParams";

/**
 * Inventory search components rendered in the navbar.
 *
 * Returns undefined if the user is adjusting bins.
 */
export function InventoryNavbarSearch() {
  const { t } = useTranslation();
  const { errorToast, warningToast } = useToast();
  const dispatch = useAppDispatch();
  const trackedPageName = useMixpanelPageName();
  const { isMultiTempGrid, tempZones } = useGetMultiTempGridInfo();
  const { getInventoryParams, isLoadingBinIds } = useGetInventoryParams();

  const { data: holdReasonCodeOptions, error: holdReasonCodeError } =
    useGetAvailableHoldReasonCodesQuery();

  const isAdjustingBins = useAppSelector(selectIsAdjustingBins);
  const selectedRows = useAppSelector(selectSelectedInventoryRows);
  const selectedHoldTypeFilters = useAppSelector(selectHoldTypeFilters);
  const selectedTempZoneFilters = useAppSelector(selectTempZoneFilters);

  const isHiddenForAdjustingBins = isAdjustingBins && selectedRows.length > 0;
  const shouldFetchFilterCounts =
    !isLoadingBinIds &&
    !isHiddenForAdjustingBins &&
    (isMultiTempGrid || !!holdReasonCodeOptions?.length);

  const { data: filterCounts, error: filterCountsError } =
    useGetInventoryFilterCountsQuery(
      shouldFetchFilterCounts
        ? {
            ...getInventoryParams,
            temperatureZones:
              isMultiTempGrid && selectedTempZoneFilters.length
                ? selectedTempZoneFilters.map(tempZoneToTemperatureZone)
                : undefined,
            hold: selectedHoldTypeFilters.length
              ? selectedHoldTypeFilters
              : undefined
          }
        : skipToken
    );
  const tempZoneCounts = countByFilter(filterCounts?.temperatureZones);
  const holdTypeCounts = countByFilter(filterCounts?.holdTypes);

  useEffect(() => {
    if (holdReasonCodeError) {
      errorToast(getMessageFromRtkError(holdReasonCodeError));
    }
  }, [holdReasonCodeError, errorToast]);

  useEffect(() => {
    if (filterCountsError) {
      warningToast(getMessageFromRtkError(filterCountsError));
    }
  }, [filterCountsError, warningToast]);

  if (isHiddenForAdjustingBins) {
    return undefined;
  }

  const tempZoneMenuItems = tempZones
    .map(parseProductTempZone)
    .filter(nonNullable)
    .map<FilterMenuItem>((zone) => ({
      translation: t(zone),
      filter: zone,
      count: tempZoneCounts.get(zone.toLowerCase())
    }));

  const holdTypeMenuItems = holdReasonCodeOptions
    ?.map<FilterMenuItem>((option) => ({
      translation: t(option),
      filter: option,
      count: holdTypeCounts.get(option.toLowerCase())
    }))
    .sort((a, b) => a.translation.localeCompare(b.translation));

  const tempZoneFilterProps = {
    menuItemList: tempZoneMenuItems,
    menuItemsTitle: t("temperature"),
    selectedMenuItems: selectedTempZoneFilters,
    onFilterData: (filters: string[]) =>
      dispatch(
        setTempZoneFilters(
          filters.map(parseProductTempZone).filter(nonNullable)
        )
      )
  };

  const holdTypeFilterProps = {
    menuItemList: holdTypeMenuItems,
    menuItemsTitle: t("hold type"),
    selectedMenuItems: selectedHoldTypeFilters,
    onFilterData: (filters: string[]) => {
      dispatch(setHoldTypeFilters(filters));
    }
  };

  // NavbarFilters requires primary and secondary filters. We pass multi-temp as
  // the primary filter only when the grid is multi-temp.
  const primaryFilterProps = isMultiTempGrid
    ? tempZoneFilterProps
    : holdTypeFilterProps;

  const secondaryFilterProps = isMultiTempGrid
    ? {
        secondaryMenuItemList: holdTypeFilterProps.menuItemList,
        secondaryMenuItemsTitle: holdTypeFilterProps.menuItemsTitle,
        selectedSecondaryMenuItems: holdTypeFilterProps.selectedMenuItems,
        onSecondaryFilterData: holdTypeFilterProps.onFilterData
      }
    : undefined;
  const hasActiveFilters =
    !!selectedTempZoneFilters.length || !!selectedHoldTypeFilters.length;

  return (
    <Box
      id="inventoryLookup-navbar-searchComponent"
      display="flex"
      flexGrow="1"
      justifyContent="center"
    >
      <ProductBinSearchInput />
      {(isMultiTempGrid || holdReasonCodeOptions) && (
        <NavbarFilters
          {...primaryFilterProps}
          {...secondaryFilterProps}
          hasActiveFilters={hasActiveFilters}
          onFiltersMenuClose={() => {
            if (hasActiveFilters) {
              mixpanelTrack(
                formatInventoryNavbarFilterSelectionMixpanelPayload(
                  trackedPageName,
                  [...selectedTempZoneFilters, ...selectedHoldTypeFilters]
                )
              );
            }
          }}
          onResetFilters={() => {
            mixpanelTrack({
              trackedPageName,
              type: "Button Click",
              label: "Reset inventory filters"
            });
            dispatch(setTempZoneFilters([]));
            dispatch(setHoldTypeFilters([]));
          }}
        />
      )}
    </Box>
  );
}
